/**
 * @see https://redux-toolkit.js.org/tutorials/typescript
 */
import { enableMapSet } from 'immer';
import logger from 'redux-logger';

import { isDevMode } from '@common/utils';
import { easyEstimatorApi } from '@ee-api/easyEstimatorService';
import { configureStore } from '@reduxjs/toolkit';

import { staticContentService } from '../api/static-content-api/staticContentService';
import chipMinimizerReducer from './slices/chipMinimizerSlice';
import colorReducer from './slices/colorSlice';
import cookingApplianceReducer from './slices/cookingApplianceSlice';
import cornerRadiiReducer from './slices/cornerRadiiSlice';
import customSideReducer from './slices/customSideSlice';
import cutoutReducer from './slices/cutoutSlice';
import diagonalCornerReducer from './slices/diagonalCornerSlice';
import dishwasherReducer from './slices/dishwasherSlice';
import drawingShapeReducer from './slices/drawingShapeSlice';
import edgeReducer from './slices/edgeSlice';
import estimateReducer from './slices/estimateSlice';
import fabricReducer from './slices/fabricSlice';
import faucetReducer from './slices/faucetSlice';
import materialReducer from './slices/materialSlice';
import plumbingReducer from './slices/plumbingSlice/plumbingSlice';
import pricedProductsReducer from './slices/pricedProductsSlice';
import refrigeratorReducer from './slices/refrigeratorSlice';
import shapeReducer from './slices/shapeSlice';
import sinkReducer from './slices/sinkSlice';
import storeReducer from './slices/storeSlice';
import tearOutReducer from './slices/tearOutSlice';
import toolbarCanvasReducer from './slices/toolbarCanvasSlice';
import zipcodeReducer from './slices/zipcodeSlice';

enableMapSet();

export const reduxStore = configureStore({
    reducer: {
        chipMinimizer: chipMinimizerReducer,
        color: colorReducer,
        cookingAppliance: cookingApplianceReducer,
        cornerRadius: cornerRadiiReducer,
        customSide: customSideReducer,
        cutout: cutoutReducer,
        diagonalCorner: diagonalCornerReducer,
        dishwasher: dishwasherReducer,
        drawingShape: drawingShapeReducer,
        edge: edgeReducer,
        estimate: estimateReducer,
        fabric: fabricReducer,
        faucet: faucetReducer,
        material: materialReducer,
        plumbing: plumbingReducer,
        pricedProducts: pricedProductsReducer,
        refrigerator: refrigeratorReducer,
        shape: shapeReducer,
        sink: sinkReducer,
        store: storeReducer,
        tearOut: tearOutReducer,
        toolbarCanvas: toolbarCanvasReducer,
        zipcode: zipcodeReducer,
        [easyEstimatorApi.reducerPath]: easyEstimatorApi.reducer,
        [staticContentService.reducerPath]: staticContentService.reducer,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false })
            .concat(easyEstimatorApi.middleware)
            .concat(staticContentService.middleware)
            .concat(isDevMode() ? [logger as never] : []),
    devTools: {
        stateSanitizer: (state) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const fabricNames = (fabricObject: any, prefix?: string) => {
                if (!fabricObject) {
                    return [];
                }
                if (prefix === undefined || fabricObject.type === 'group') {
                    const path = prefix === undefined ? '' : `${prefix}/${fabricObject.name ?? 'nogroupname'}`;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    return fabricObject.getObjects().map((fo: any) => {
                        return fabricNames(fo, path);
                    });
                }
                const path = `${prefix}/${fabricObject.name ?? 'noobjectname'}`;
                return `${path}: ${fabricObject.type}`;
            };

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sanitizedState = state as any;
            if (sanitizedState.fabric && sanitizedState.fabric.canvas) {
                return {
                    ...sanitizedState,
                    fabric: {
                        ...sanitizedState.fabric,
                        canvas: fabricNames(sanitizedState.fabric.canvas),
                    },
                };
            }
            return sanitizedState;
        },
    },
});

export type RootState = ReturnType<typeof reduxStore.getState>;
export type AppDispatch = typeof reduxStore.dispatch;
